/**
 * Batch-6 verlof — pure berekeningen voor seniorendagen (CAO Metaal &
 * Techniek art. 51). De staffel zelf is een instelbare, versioneerbare tabel
 * (`SeniorenRegelStaffel`, geldigVan/geldigTm) — hier staan uitsluitend de
 * match- en pro-rata-regels, GEEN CAO-cijfers (CC.6).
 *
 * Peildata ("DD-MM", default 30-06 en 31-12) en de vervaldatum-conventie
 * ("DD-MM", eerstvolgende ná de peildatum) komen uit het VerlofBeleid.
 *
 * Datums zijn UTC-middernacht van de NL-kalenderdag (repo-conventie).
 */

import { round2 } from './leave-calculations'

export interface SeniorenStaffelRijInput {
  id: string
  leeftijdVanaf: number
  leeftijdTm: number
  minimaleDienstMaanden: number
  urenPerPeildatumFulltime: number
  proRata: boolean
  uitbetalenToegestaan: boolean
  keuzeMedewerkerToegestaan: boolean
  geldigVan: Date
  geldigTm: Date | null
}

/** Leeftijd in hele jaren op de peildatum (UTC-datumvelden). */
export function leeftijdOpDatum(geboortedatum: Date, peildatum: Date): number {
  let leeftijd = peildatum.getUTCFullYear() - geboortedatum.getUTCFullYear()
  const maandDelta = peildatum.getUTCMonth() - geboortedatum.getUTCMonth()
  if (maandDelta < 0 || (maandDelta === 0 && peildatum.getUTCDate() < geboortedatum.getUTCDate())) {
    leeftijd -= 1
  }
  return leeftijd
}

/** Volledige dienstmaanden tussen indiensttreding en peildatum. */
export function dienstMaandenOpDatum(startDate: Date, peildatum: Date): number {
  if (peildatum.getTime() < startDate.getTime()) return 0
  let maanden =
    (peildatum.getUTCFullYear() - startDate.getUTCFullYear()) * 12 +
    (peildatum.getUTCMonth() - startDate.getUTCMonth())
  if (peildatum.getUTCDate() < startDate.getUTCDate()) maanden -= 1
  return Math.max(0, maanden)
}

/**
 * Vind de op de peildatum geldige staffel-rij voor deze leeftijd/diensttijd.
 * Versionering: een rij geldt wanneer geldigVan <= peildatum <= geldigTm
 * (geldigTm null = open einde). Bij meerdere matches wint de hoogste
 * `leeftijdVanaf` (meest specifieke leeftijdsband), daarna de meest recente
 * `geldigVan` (nieuwste regelversie).
 */
export function vindSeniorenStaffelRij(
  staffels: SeniorenStaffelRijInput[],
  criteria: { leeftijd: number; dienstMaanden: number; peildatum: Date },
): SeniorenStaffelRijInput | null {
  const kandidaten = staffels.filter((rij) => {
    if (rij.geldigVan.getTime() > criteria.peildatum.getTime()) return false
    if (rij.geldigTm && rij.geldigTm.getTime() < criteria.peildatum.getTime()) return false
    if (criteria.leeftijd < rij.leeftijdVanaf || criteria.leeftijd > rij.leeftijdTm) return false
    if (criteria.dienstMaanden < rij.minimaleDienstMaanden) return false
    return true
  })
  if (kandidaten.length === 0) return null
  kandidaten.sort(
    (a, b) =>
      b.leeftijdVanaf - a.leeftijdVanaf || b.geldigVan.getTime() - a.geldigVan.getTime(),
  )
  return kandidaten[0]
}

/**
 * Pro-rata-uren voor één peildatum: `urenPerPeildatumFulltime ×
 * (contracturen / voltijdsUren)` wanneer de rij proRata heeft, anders het
 * volle aantal. Voltijds-uren komen uit het VerlofBeleid (geen 40 hardcoden).
 */
export function berekenSeniorenUren(
  rij: Pick<SeniorenStaffelRijInput, 'urenPerPeildatumFulltime' | 'proRata'>,
  contractUrenPerWeek: number,
  voltijdsUren: number,
): number {
  if (!rij.proRata) return round2(rij.urenPerPeildatumFulltime)
  if (voltijdsUren <= 0) return 0
  return round2(rij.urenPerPeildatumFulltime * (contractUrenPerWeek / voltijdsUren))
}

/** Parse "DD-MM" naar {dag, maand} of null bij ongeldig formaat. */
export function parseDagMaand(s: string): { dag: number; maand: number } | null {
  const m = /^(\d{2})-(\d{2})$/.exec(s.trim())
  if (!m) return null
  const dag = Number(m[1])
  const maand = Number(m[2])
  if (dag < 1 || dag > 31 || maand < 1 || maand > 12) return null
  return { dag, maand }
}

/** Is `datum` (UTC-kalenderdag) één van de geconfigureerde "DD-MM"-peildata? */
export function isPeildatum(datum: Date, peildata: string[]): boolean {
  return peildata.some((p) => {
    const dm = parseDagMaand(p)
    return dm !== null && datum.getUTCDate() === dm.dag && datum.getUTCMonth() + 1 === dm.maand
  })
}

/**
 * UTC-datum van een "DD-MM" in een gegeven jaar (bv. peildatum "30-06" in
 * 2026 → 2026-06-30T00:00Z). Ongeldig formaat → null.
 */
export function dagMaandInJaar(dagMaand: string, jaar: number): Date | null {
  const dm = parseDagMaand(dagMaand)
  if (!dm) return null
  return new Date(Date.UTC(jaar, dm.maand - 1, dm.dag))
}

/**
 * Vervaldatum van een senioren-toekenning: `maanden` ná de peildatum. CAO art.
 * 51 geeft "binnen één jaar na verkrijging" (default 12 maanden), dus elke
 * tranche (30-06 én 31-12) krijgt een vol jaar. Een vaste kalenderdatum kon dat
 * voor twee peildata niet correct uitdrukken (de 30-06-tranche verviel dan al
 * na een half jaar).
 */
export function vervalNaPeildatum(peildatum: Date, maanden: number): Date {
  return new Date(
    Date.UTC(
      peildatum.getUTCFullYear(),
      peildatum.getUTCMonth() + maanden,
      peildatum.getUTCDate(),
    ),
  )
}
